import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import {
  LayoutDashboard, Users, KanbanSquare, PhoneCall, CalendarClock, FileText, PlusCircle, ListTodo, UserSquare, Upload, Copy,
  Handshake, UserCog, Landmark, Wallet, Calculator, BarChart3, Bot, LifeBuoy, Webhook, Plug, ScrollText, Settings, ShieldCheck,
  Activity, BookOpen, Bell, Search, LogOut, Menu, X, User, FileCheck,
} from "lucide-react";
import { useAuth } from "@/context/Auth";
import { get, post, dt, BASE, label } from "@/lib/api";
import { Logo } from "@/components/kit";

const STAFF_NAV = [
  ["Dashboard", "/crm", LayoutDashboard, null],
  ["Leads", "/crm/leads", Users, "lead.view"],
  ["Kanban", "/crm/kanban", KanbanSquare, "lead.edit"],
  ["Follow-ups", "/crm/followups", CalendarClock, "followup.manage"],
  ["Telecalling", "/crm/telecalling", PhoneCall, "telecalling.use"],
  ["Applications", "/crm/applications", FileText, "application.view"],
  ["New Application", "/crm/applications/new", PlusCircle, "application.create"],
  ["Tasks", "/crm/tasks", ListTodo, "task.manage"],
  ["Customers", "/crm/customers", UserSquare, "customer.view"],
  ["CSV Import", "/crm/import", Upload, "lead.edit"],
  ["Duplicates", "/crm/duplicates", Copy, "lead.assign"],
  ["DSA Partners", "/crm/dsa", Handshake, "dsa.manage"],
  ["Employees", "/crm/employees", UserCog, "user.manage"],
  ["Lenders & APIs", "/crm/lenders", Landmark, "lender.manage"],
  ["Payouts", "/crm/payouts", Wallet, "payout.view"],
  ["Finance", "/crm/finance", Calculator, "finance.view"],
  ["Reports", "/crm/reports", BarChart3, "report.view"],
  ["AI Assistant", "/crm/ai", Bot, "ai.use"],
  ["Tickets", "/crm/tickets", LifeBuoy, "ticket.manage"],
  ["Webhooks", "/crm/webhooks", Webhook, "webhook.manage"],
  ["API Partners", "/crm/partners", Plug, "partner.manage"],
  ["Audit Logs", "/crm/audit", ScrollText, "audit.view"],
  ["Settings", "/crm/settings", Settings, "settings.manage"],
  ["Roles & Permissions", "/crm/roles", ShieldCheck, "role.manage"],
  ["System Health", "/crm/health", Activity, "admin.manage"],
];
const CUSTOMER_NAV = [
  ["My Applications", "/portal", FileText, null],
  ["Apply for Loan", "/apply/PL", PlusCircle, null],
  ["Consents", "/portal/consents", FileCheck, null],
  ["Support", "/portal/support", LifeBuoy, null],
];

function Bell_() {
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const load = () => get("/notifications/me").then(setData).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);
  const readAll = async () => { await post("/notifications/read-all"); load(); };
  return (
    <div className="relative">
      <button data-testid="notification-bell" onClick={() => setOpen(!open)} className="relative rounded-full p-2 hover:bg-slate-100">
        <Bell className="h-5 w-5 text-slate-600" />
        {data.unread > 0 && <span data-testid="notification-count" className="absolute -right-0.5 -top-0.5 rounded-full bg-rose-600 px-1.5 text-[10px] font-bold text-white">{data.unread}</span>}
      </button>
      {open && (
        <div className="absolute right-0 z-50 mt-2 w-80 rounded-xl border bg-white shadow-xl" data-testid="notification-panel">
          <div className="flex items-center justify-between border-b px-4 py-2">
            <p className="text-sm font-semibold">Notifications</p>
            <button data-testid="notification-read-all" onClick={readAll} className="text-xs text-blue-800">Mark all read</button>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {data.items.length === 0 && <p className="p-6 text-center text-xs text-slate-400">No notifications</p>}
            {data.items.map((n) => (
              <button key={n.id} onClick={() => { setOpen(false); n.link && nav(n.link); }} className={`block w-full border-b px-4 py-3 text-left hover:bg-slate-50 ${n.read ? "" : "bg-blue-50/50"}`}>
                <p className="text-sm font-semibold text-slate-800">{n.title}</p>
                <p className="text-xs text-slate-500">{n.body}</p>
                <p className="mt-1 text-[10px] text-slate-400">{dt(n.created_at)}</p>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function GlobalSearch() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  const nav = useNavigate();
  useEffect(() => {
    if (q.length < 3) { setRes([]); return; }
    const t = setTimeout(() => get("/search", { q }).then(setRes).catch(() => {}), 300);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="relative hidden w-full max-w-md md:block">
      <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
      <input data-testid="global-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, mobile, PAN, Lead/App/DSA/Employee ID…"
        className="h-9 w-full rounded-full border border-slate-200 bg-slate-50 pl-9 pr-3 text-sm outline-none focus:border-blue-800 focus:bg-white" />
      {res.length > 0 && (
        <div className="absolute z-50 mt-1 w-full rounded-xl border bg-white shadow-xl" data-testid="global-search-results">
          {res.map((r) => (
            <button key={r.type + r.id} onClick={() => { setQ(""); nav(r.link); }} className="flex w-full items-center justify-between border-b px-4 py-2 text-left hover:bg-slate-50">
              <div><p className="text-sm font-medium">{r.title}</p><p className="text-xs text-slate-500">{label(r.subtitle)}</p></div>
              <span className="text-[10px] font-semibold uppercase text-slate-400">{r.type}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AppShell() {
  const { user, logout, can } = useAuth();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const items = user.role === "customer" ? CUSTOMER_NAV : STAFF_NAV.filter(([, , , p]) => !p || can(p));
  const profile = user.role === "customer" ? "/portal/profile" : "/crm/profile";
  const Side = (
    <nav className="flex-1 space-y-0.5 overflow-y-auto px-3 py-4">
      {items.map(([l, to, Icon]) => (
        <NavLink key={to} to={to} end={["/crm", "/portal", "/crm/applications"].includes(to)} onClick={() => setOpen(false)} data-testid={`nav-${l.toLowerCase().replace(/[^a-z]+/g, "-")}`}
          className={({ isActive }) => `flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${isActive ? "bg-blue-800 text-white" : "text-slate-300 hover:bg-slate-800 hover:text-white"}`}>
          <Icon className="h-4 w-4" />{l}
        </NavLink>
      ))}
      {user.role !== "customer" && (
        <a href={`${BASE}/api/docs`} target="_blank" rel="noreferrer" data-testid="nav-api-docs" className="flex items-center gap-3 rounded-md px-3 py-2 text-sm text-slate-300 hover:bg-slate-800">
          <BookOpen className="h-4 w-4" />API Docs
        </a>
      )}
    </nav>
  );
  return (
    <div className="flex h-screen overflow-hidden bg-slate-50">
      <aside className={`fixed inset-y-0 left-0 z-40 flex w-64 flex-col bg-slate-900 transition-transform md:static md:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="flex items-center justify-between border-b border-slate-800 px-5 py-4">
          <Link to="/"><Logo dark /></Link>
          <button className="text-slate-400 md:hidden" onClick={() => setOpen(false)}><X className="h-5 w-5" /></button>
        </div>
        {Side}
        <div className="border-t border-slate-800 px-5 py-3 text-[10px] leading-relaxed text-slate-500">LSP / Loan distribution platform. Credit decisions are made by partner lenders.</div>
      </aside>
      {open && <div className="fixed inset-0 z-30 bg-black/40 md:hidden" onClick={() => setOpen(false)} />}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 flex h-14 items-center gap-3 border-b border-slate-200 bg-white/90 px-4 backdrop-blur-md md:px-6">
          <button data-testid="mobile-menu-btn" className="md:hidden" onClick={() => setOpen(true)}><Menu className="h-5 w-5" /></button>
          {user.role !== "customer" ? <GlobalSearch /> : <div className="flex-1" />}
          <div className="ml-auto flex items-center gap-2">
            <Bell_ />
            <button data-testid="profile-btn" onClick={() => nav(profile)} className="flex items-center gap-2 rounded-full px-2 py-1 hover:bg-slate-100">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 text-blue-900"><User className="h-4 w-4" /></div>
              <div className="hidden text-left sm:block">
                <p className="text-xs font-semibold leading-tight" data-testid="current-user-name">{user.name}</p>
                <p className="text-[10px] uppercase tracking-wider text-slate-500" data-testid="current-user-role">{label(user.role)}{user.dsa_code ? ` · ${user.dsa_code}` : user.employee_code ? ` · ${user.employee_code}` : ""}</p>
              </div>
            </button>
            <button data-testid="logout-btn" onClick={async () => { await logout(); nav("/login"); }} className="rounded-full p-2 hover:bg-slate-100" title="Logout"><LogOut className="h-4 w-4 text-slate-600" /></button>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-4 md:p-8"><Outlet /></main>
      </div>
    </div>
  );
}
